import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { setWorkingHours, getWorkingHours } from '../../services/api';

const DAYS_OF_WEEK = [
  'Sunday',    // 1
  'Monday',    // 2
  'Tuesday',   // 3
  'Wednesday', // 4
  'Thursday',  // 5
  'Friday',    // 6
  'Saturday'   // 7
];

interface DayHours {
  day_of_week: number;
  start_time: Date | null;
  end_time: Date | null;
  is_working: boolean;
}

const WorkingHours: React.FC = () => {
  const [workingHours, setWorkingHoursState] = useState<DayHours[]>(
    DAYS_OF_WEEK.map((_, index) => ({
      day_of_week: index + 1, // Add 1 to match the 1-based indices
      start_time: null,
      end_time: null,
      is_working: true,
    }))
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchWorkingHours();
    }
  }, [user]);

  const fetchWorkingHours = async () => {
    try {
      const data = await getWorkingHours(parseInt(user?.id || '0'));
      const updatedHours = [...workingHours];
      data.forEach((hours) => {
        // Adjust the index to match 1-based array
        const index = hours.day_of_week - 1;
        if (index >= 0 && index < 7) {
          updatedHours[index] = {
            ...hours,
            start_time: new Date(`2024-01-01T${hours.start_time}`),
            end_time: new Date(`2024-01-01T${hours.end_time}`),
          };
        }
      });
      setWorkingHoursState(updatedHours);
    } catch (error) {
      console.error('Error fetching working hours:', error);
      setError('Failed to fetch working hours');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const hoursToSubmit = workingHours.map((day) => ({
        day_of_week: day.day_of_week, // Already 1-based
        start_time: day.start_time ? format(day.start_time, 'HH:mm') : '09:00',
        end_time: day.end_time ? format(day.end_time, 'HH:mm') : '17:00',
        is_working: day.is_working,
      }));

      await setWorkingHours(hoursToSubmit);
      setSuccess('Working hours saved successfully');
    } catch (error) {
      setError('Failed to save working hours');
    } finally {
      setLoading(false);
    }
  };

  const handleWorkingDayChange = (dayIndex: number, isWorking: boolean) => {
    const updatedHours = [...workingHours];
    updatedHours[dayIndex] = {
      ...updatedHours[dayIndex],
      is_working: isWorking,
    };
    setWorkingHoursState(updatedHours);
  };

  const handleTimeChange = (dayIndex: number, field: 'start_time' | 'end_time', value: Date | null) => {
    const updatedHours = [...workingHours];
    updatedHours[dayIndex] = {
      ...updatedHours[dayIndex],
      [field]: value,
    };
    setWorkingHoursState(updatedHours);
  };

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Set Working Hours
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {workingHours.map((day, index) => (
              <Grid item xs={12} key={index}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={day.is_working}
                          onChange={(e) => handleWorkingDayChange(index, e.target.checked)}
                        />
                      }
                      label={DAYS_OF_WEEK[index]}
                    />
                  </Grid>
                  {day.is_working && (
                    <>
                      <Grid item xs={12} md={4}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <TimePicker
                            label="Start Time"
                            value={day.start_time}
                            onChange={(newValue) => handleTimeChange(index, 'start_time', newValue)}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <TimePicker
                            label="End Time"
                            value={day.end_time}
                            onChange={(newValue) => handleTimeChange(index, 'end_time', newValue)}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Working Hours'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WorkingHours; 